// Client email activity for Generate (spec §3.3, D2): the signed-in user's own mailbox only, and
// only messages to/from the client's contacts. Reads subject + preview, never bodies or
// attachments; addresses are used for matching and never leave the browser.
import { addDays, localDate } from "./period.js";

export const WEBMAIL_DOMAINS = new Set(["gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com", "msn.com", "yahoo.com", "icloud.com", "me.com", "aol.com", "proton.me", "protonmail.com"]);
// .mil/.gov/.edu domains are shared by too many unrelated people to match on the whole domain —
// treated like webmail: the exact POC address still matches, but the domain never gets added.
const GOV_EDU_MIL = /\.(mil|gov|edu)$/i;
const MAX_EMAILS = 60;

export function clientMatchers(pocs, ownDomain = "sawtoothadvisors.io") {
  const emails = new Set();
  const domains = new Set();
  for (const p of pocs || []) {
    const e = String(p?.email || "").trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) continue;
    const d = e.split("@")[1];
    // Own-domain POC addresses (Sawtooth staff listed as a POC) aren't client contacts at all.
    if (d === ownDomain) continue;
    emails.add(e);
    if (!WEBMAIL_DOMAINS.has(d) && !GOV_EDU_MIL.test(d)) domains.add(d);
  }
  return { emails, domains };
}

// Scrub email addresses and links out of subject/preview text before it leaves the browser: the
// proxy (and so Anthropic) never sees SharePoint links or client email addresses.
const EMAIL_OR_MAILTO = /\bmailto:\S+|\b[\w.+-]+@[\w-]+(?:\.[\w-]+)+\b/gi;
const SCHEME_URL = /\b[a-zA-Z][a-zA-Z0-9+.-]*:\/\/\S+/g;
const FILE_SHARE_HOST = /\b[\w-]+(?:\.[\w-]+)*\.(?:sharepoint\.com|sharepoint\.us)\S*|\bonedrive\S*|\b1drv\.ms\S*/gi;
const WWW_HOST = /\bwww\.\S+/gi;

function scrub(text) {
  return String(text ?? "")
    .replace(EMAIL_OR_MAILTO, "[email]")
    .replace(SCHEME_URL, "[link]")
    .replace(FILE_SHARE_HOST, "[link]")
    .replace(WWW_HOST, "[link]");
}

const RANGES = 4;
const PAGE_SIZE = 250;
const PAGES_PER_RANGE = 4;

// Names the failure in the notice so a skipped mailbox can be diagnosed from the screen.
// graph.js throws status 0 on its own timeout and the HTTP status otherwise; a fetch that
// never got a response (offline, blocked) has no status at all.
export function failReason(e) {
  if (e?.status === 0) return "timed out";
  if (e?.status) return `error ${e.status}`;
  return "connection problem";
}

export async function collectClientEmails({ graphGet, pocs, periodStart, periodEnd }) {
  const m = clientMatchers(pocs);
  if (!m.emails.size) return { emails: [], notice: "Email skipped — this client has no contact email addresses on its POCs tab." };
  const startMs = new Date(`${periodStart}T00:00:00`).getTime();
  const endMs = new Date(`${addDays(periodEnd, 1)}T00:00:00`).getTime();
  const SELECT = "$select=id,subject,bodyPreview,from,toRecipients,ccRecipients,receivedDateTime,isDraft,parentFolderId";

  // First choice: ask Graph to search the mailbox for the client's contacts (exact POC addresses
  // plus company domains), so the work scales with this client's mail, not the whole mailbox.
  // $search can't be combined with a date $filter on messages, so the period is applied here.
  let page = null;
  let partial = false;
  try {
    const terms = [...m.emails, ...m.domains].map((t) => `participants:${t}`).join(" OR ");
    const found = await graphGet(`/me/messages?$search=${encodeURIComponent(`"${terms}"`)}&${SELECT}&$top=${PAGE_SIZE}`, { all: true, maxPages: PAGES_PER_RANGE });
    const inPeriod = (found.value || []).filter((x) => { const t = new Date(x.receivedDateTime).getTime(); return t >= startMs && t < endMs; })
      .sort((x, y) => (x.receivedDateTime < y.receivedDateTime ? 1 : x.receivedDateTime > y.receivedDateTime ? -1 : 0));
    page = { value: inPeriod, truncated: !!found.truncated };
  } catch (e) {
    // Only a rejected search (4xx) falls back; a timeout or server error would make the slower
    // scan below blow Generate's 45s budget too, so report it instead.
    if (!(e?.status >= 400 && e.status < 500)) return { emails: [], notice: `Email skipped — your mailbox didn’t respond (${failReason(e)}).` };
    // Fallback: the date-range scan of the whole mailbox. A busy mailbox can hold well over a
    // thousand messages in two weeks, so the period is split into RANGES contiguous ranges read
    // at the same time, in big pages.
    const edges = Array.from({ length: RANGES + 1 }, (_, i) => new Date(i === RANGES ? endMs : startMs + Math.floor(((endMs - startMs) * i) / RANGES)).toISOString());
    const pathFor = (from, until) => {
      const filter = encodeURIComponent(`receivedDateTime ge ${from} and receivedDateTime lt ${until}`);
      return `/me/messages?$filter=${filter}&$orderby=receivedDateTime%20desc&${SELECT}&$top=${PAGE_SIZE}`;
    };
    const results = await Promise.allSettled(edges.slice(0, RANGES).map((from, i) => graphGet(pathFor(from, edges[i + 1]), { all: true, maxPages: PAGES_PER_RANGE })));
    const ok = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
    if (!ok.length) return { emails: [], notice: `Email skipped — your mailbox didn’t respond (${failReason(results[0].reason)}).` };
    partial = ok.length < RANGES;
    const seen = new Set();
    const merged = ok.flatMap((p) => p.value || [])
      .filter((x) => (seen.has(x.id) ? false : (seen.add(x.id), true)))
      .sort((x, y) => (x.receivedDateTime < y.receivedDateTime ? 1 : x.receivedDateTime > y.receivedDateTime ? -1 : 0));
    page = { value: merged, truncated: ok.some((p) => p.truncated) };
  }

  const addr = (r) => String(r?.emailAddress?.address || "").toLowerCase();
  const isClient = (a) => !!a && (m.emails.has(a) || m.domains.has(a.split("@")[1]));
  const value = page.value || [];
  const emails = [];
  let cappedWithMoreLeft = false;
  for (let i = 0; i < value.length; i++) {
    const msg = value[i];
    if (msg.isDraft === true) continue;
    const sender = addr(msg.from);
    const all = [sender, ...(msg.toRecipients || []).map(addr), ...(msg.ccRecipients || []).map(addr)];
    if (!all.some(isClient)) continue;
    emails.push({
      // Ids are short and sequential (output order, 1-based) — Graph's own message id never
      // leaves the browser.
      id: `email:${emails.length + 1}`,
      date: localDate(new Date(msg.receivedDateTime)),
      direction: isClient(sender) ? "from client" : "to client",
      subject: scrub(String(msg.subject || "")).slice(0, 200),
      preview: scrub(String(msg.bodyPreview || "").replace(/\s+/g, " ").trim()).slice(0, 300),
    });
    if (emails.length >= MAX_EMAILS) {
      cappedWithMoreLeft = i < value.length - 1;
      break;
    }
  }
  const notice = partial ? "Email: part of your mailbox didn’t respond — some mail may be missing."
    : page.truncated || cappedWithMoreLeft ? "Email: there was a lot of mail in this period — only the most recent was checked." : null;
  return { emails, notice };
}
